import classNames from 'classnames';
import { FormikConfig, useFormikContext } from 'formik';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InfoContent } from '../../components/InfoContent';
import { ConfirmDialog } from '../../components/Modal/ConfirmDialog';
import { CreateModal } from '../../components/Modal/CreateModal';
import { FormikCheckBox } from '../../components/formik/FormCheckBox';
import { FormikDayInput } from '../../components/formik/FormDayInput';
import { FormInput, FormikInput } from '../../components/formik/FormInput';
import { useSubscriptionTypeTranslations } from '../../hooks/translations/useSubscriptionTypeTranslations';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { DateValue, useFormatDate } from '../../hooks/useFormatDate';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { T, useT } from '../../translation/src';
import {
  PlanChangeProblem,
  SuperadminBillingDatePreviewQuery,
  useSuperadminBillingDatePreviewQuery,
  useSuperadminCancelSubscriptionMutation,
  useSuperadminPauseSubscriptionMutation,
  useSuperadminSetBillingDateMutation,
} from '../../types/graphqlTypes';
import { dayAfterTermEnd, toDateInput, tomorrowUtc } from '../../utils/dates';
import { formatPrice } from '../../utils/formatPrice';
import { NoBillingDetailsWarning } from './NoBillingDetailsWarning';

// Every mutation here returns the organisation as it stands (the card's fragment): the cache updates the card
// in place and nothing is refetched — refetching every active query reloaded the whole organisations list.

const Section: React.FC2<{ title: React.ReactNode; tone?: 'default' | 'highlight' }> = ({ title, tone = 'default', children, className }) => (
  <div className={classNames('flex flex-col gap-1 rounded-md border p-3', tone === 'highlight' ? 'border-neon-green-500 bg-neon-green-50/50 dark:bg-transparent' : 'border-gray-300 dark:border-gray-600', className)}>
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</span>
    {children}
  </div>
);

const Row: React.FC2<{ label: React.ReactNode; emphasis?: boolean }> = ({ label, emphasis, children, className }) => (
  <div className={classNames('flex items-baseline justify-between gap-4 text-sm', emphasis && 'font-semibold', className)}>
    <span className={classNames(!emphasis && 'text-gray-600 dark:text-gray-300')}>{label}</span>
    <span className="text-right">{children}</span>
  </div>
);

const Hint: React.FC2 = ({ children, className }) => <span className={classNames('text-xs text-gray-500', className)}>{children}</span>;

// Static keys, so the extraction finds them. Only the problems a billing date can have.
const useBillingDateProblemMessages = (): Partial<Record<PlanChangeProblem, string>> => {
  const noTerm = useT('There is no running term to move the billing date of.');
  const paused = useT('The subscription is paused: the billing date can be moved once it resumes.');
  const inPast = useT('The billing date must be in the future.');
  const beforeStart = useT('The billing date must be after the day the term started.');
  const pendingAfter = useT('An upgrade is pending on or after that day. End the term after it, or cancel the upgrade first.');
  const invalidCharge = useT('The price may not be negative.');
  const billingUnset = useT('Fill in the billing details first, or mark them as left blank on purpose (the months added are then free).');
  return useMemo(
    () => ({
      [PlanChangeProblem.BillingDetailsUnset]: billingUnset,
      [PlanChangeProblem.NoTerm]: noTerm,
      [PlanChangeProblem.SubscriptionPaused]: paused,
      [PlanChangeProblem.EndDateInPast]: inPast,
      [PlanChangeProblem.EndDateBeforeStart]: beforeStart,
      [PlanChangeProblem.PendingChangeAfterEnd]: pendingAfter,
      [PlanChangeProblem.InvalidCharge]: invalidCharge,
    }),
    [noTerm, paused, inPast, beforeStart, pendingAfter, invalidCharge, billingUnset],
  );
};

// ---------------------------------------------------------------------------------------------------------
// Billing date

type BillingDatePreview = SuperadminBillingDatePreviewQuery['superadminBillingDatePreview'];

interface BillingDateFormValues {
  // The day the term renews from now on (an ISO day); the term runs until the day before.
  renewalDate: string;
  // Bill `price` (euros) instead of the computed amount for the months gained.
  overridePrice: boolean;
  price: number | '';
}

const toCents = (euros: number | '') => (euros === '' ? undefined : Math.round(Number(euros) * 100));

const useBillingDatePreview = ({ organisationId, open }: { organisationId: string; open?: boolean }): { preview?: BillingDatePreview; settled: boolean } => {
  const { values } = useFormikContext<BillingDateFormValues>();
  const variables = useMemo(() => ({ organisationId, renewalDate: values.renewalDate || tomorrowUtc(), chargeCents: values.overridePrice ? toCents(values.price) : undefined }), [organisationId, values]);
  const debounced = useDebouncedValue(variables);
  // Always from the server: the simulation depends on today.
  const { data, previousData, loading } = useSuperadminBillingDatePreviewQuery({ variables: debounced, skip: !open || !values.renewalDate, fetchPolicy: 'network-only' });
  const preview = data?.superadminBillingDatePreview ?? previousData?.superadminBillingDatePreview;
  return { preview, settled: !!preview && !!data && !loading && debounced === variables };
};

const BillingDateFields: React.FC<{ organisationId: string; open?: boolean; endDate?: DateValue | null; onReadyChange: (ready: boolean) => void }> = ({ organisationId, open, endDate, onReadyChange }) => {
  const { values, setFieldValue } = useFormikContext<BillingDateFormValues>();
  const { preview, settled } = useBillingDatePreview({ organisationId, open });
  const messages = useBillingDateProblemMessages();
  const plans = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const renewalDateLabel = useT('billing date', { swc: true });
  const overrideLabel = useT('override the price', { swc: true });
  const priceLabel = useT('price (€)', { swc: true });
  const ready = settled && !!preview && preview.problems.length === 0;
  useEffect(() => onReadyChange(ready), [onReadyChange, ready]);

  // Ticking the box starts from the computed amount — once, so the field can then be emptied to type another.
  const wasOverriding = useRef(false);
  useEffect(() => {
    if (values.overridePrice && !wasOverriding.current && values.price === '' && preview) setFieldValue('price', preview.dueCents / 100);
    wasOverriding.current = values.overridePrice;
  }, [values.overridePrice, values.price, preview, setFieldValue]);

  const gained = !!preview && preview.monthsAdded > 0;
  const lost = !!preview && preview.monthsAdded < 0;
  return (
    <div className="flex flex-col gap-3">
      <Section title={<T _str="current billing date" swc />}>
        <Row label={<T _str="renews on" swc />}>{endDate ? formatDay(dayAfterTermEnd(endDate)) : '-'}</Row>
        <Hint>
          <T _str="The day the term renews and is invoiced. The term runs until the day before." />
        </Hint>
      </Section>
      <div className="flex flex-col">
        <FormikDayInput name="renewalDate" label={renewalDateLabel} min={tomorrowUtc()} required />
        <Hint>
          <T _str="Tomorrow or later. Linked to a subscription in Odoo, its next invoice date moves to this day too." />
        </Hint>
      </div>
      {preview && preview.problems.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm text-error-red-500">
          {preview.problems.map((problem) => (
            <li key={problem}>{messages[problem] ?? problem}</li>
          ))}
        </ul>
      )}
      {preview && preview.problems.length === 0 && (
        <>
          <Section title={<T _str="after confirmation" swc />} tone="highlight">
            <Row label={<T _str="renews on" swc />}>{formatDay(new Date(preview.renewalDate))}</Row>
            <Row label={<T _str="term runs until" swc />}>{formatDay(new Date(preview.endDate))}</Row>
            <Row label={<T _str="months" swc />}>
              {gained && <T _str="{months} month(s) added" args={{ months: preview.monthsAdded }} />}
              {lost && <T _str="{months} month(s) dropped" args={{ months: -preview.monthsAdded }} />}
              {!gained && !lost && <T _str="unchanged" />}
            </Row>
            {preview.movesPendingChange && (
              <Hint>
                <T _str="The change waiting at the end of the term moves with it." />
              </Hint>
            )}
          </Section>
          <Section title={<T _str="price" swc />}>
            {gained && <Row label={`${plans[preview.subscriptionType]} · ${preview.monthsAdded} × ${formatPrice(preview.monthlyPriceCents)}`}>{formatPrice(preview.dueCents)}</Row>}
            {lost && (
              <Hint>
                <T _str="Nothing is refunded for the months dropped. The term simply renews earlier." />
              </Hint>
            )}
            {preview.priceLocked ? (
              <div className="mt-2 grid grid-cols-2 items-end gap-2">
                <NoBillingDetailsWarning />
                <FormInput name="lockedPrice" label={priceLabel} value={formatPrice(0)} disabled readOnly hideErrorLabel />
              </div>
            ) : (
              gained && (
                <div className="mt-2 grid grid-cols-2 items-end gap-2">
                  <FormikCheckBox name="overridePrice" label={overrideLabel} hideErrorLabel className="pb-3" />
                  {values.overridePrice ? (
                    <FormikInput type="number" min={0} step="0.01" name="price" label={priceLabel} required hideErrorLabel />
                  ) : (
                    <FormInput name="computedPrice" label={priceLabel} value={formatPrice(preview.dueCents)} disabled readOnly hideErrorLabel />
                  )}
                </div>
              )
            )}
            <Row label={<T _str="billed now" swc />} emphasis className="text-base">
              {formatPrice(preview.chargedCents)}
            </Row>
            <Hint>
              {preview.chargedCents > 0 ? <T _str="Recorded in the history as a {plan} subscription fee, with a draft invoice in Odoo." args={{ plan: plans[preview.subscriptionType] }} /> : <T _str="Nothing is billed or recorded." />}
            </Hint>
          </Section>
          <Section title={<T _str="in Odoo" swc />}>
            <Hint>
              {preview.odooLinked ? (
                <T _str="The subscription's next invoice date becomes {date}. Odoo invoices the next cycle that day, and the term renews here the same day." args={{ date: formatDay(new Date(preview.renewalDate)) }} />
              ) : (
                <T _str="Not linked to an Odoo subscription, so only the term here moves. Link one to keep Odoo's invoicing on the same day." />
              )}
            </Hint>
          </Section>
        </>
      )}
    </div>
  );
};

export interface BillingDateModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  endDate?: DateValue | null;
}

// The billing date moved: the term renews on the day entered, in Odoo too.
const BillingDateModal_: React.FC<BillingDateModalProps> = ({ onClose, open, organisationId, endDate }) => {
  const [setBillingDate, { loading }] = useSuperadminSetBillingDateMutation();
  const { showToast } = useToastNotifications();
  const title = useT('change billing date', { swc: true });
  const confirm = useT('confirm', { swc: true });
  const failed = useT('could not change the billing date', { swc: true });
  const [ready, setReady] = useState(false);

  const initialValues: BillingDateFormValues = useMemo(() => ({ renewalDate: endDate ? toDateInput(dayAfterTermEnd(endDate)) : '', overridePrice: false, price: '' }), [endDate]);
  const onSubmit: FormikConfig<BillingDateFormValues>['onSubmit'] = useCallback(
    async (values) => {
      try {
        await setBillingDate({ variables: { organisationId, renewalDate: values.renewalDate, chargeCents: values.overridePrice ? toCents(values.price) : undefined } });
        onClose();
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [failed, onClose, organisationId, setBillingDate, showToast],
  );
  const formik: FormikConfig<BillingDateFormValues> = useMemo(() => ({ initialValues, onSubmit, enableReinitialize: true }), [initialValues, onSubmit]);

  return (
    <CreateModal
      title={title}
      createButtonTitle={confirm}
      formik={formik}
      onClose={onClose}
      open={open}
      isSubmitting={loading}
      submitDisabled={!ready}
      panelClassName="max-w-xl"
      modalBodyClassName="max-h-[70vh] overflow-y-auto px-1 pr-3 py-1"
      showCloseIcon
    >
      <BillingDateFields organisationId={organisationId} open={open} endDate={endDate} onReadyChange={setReady} />
    </CreateModal>
  );
};
export const BillingDateModal = memo(BillingDateModal_);

// ---------------------------------------------------------------------------------------------------------
// Pause / resume

export interface PauseSubscriptionDialogProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  // Paused since then; not paused when absent.
  pausedAt?: DateValue | null;
  odooLinked: boolean;
}

// Pauses the running term, or resumes it. Odoo owns the state: it is written there first.
const PauseSubscriptionDialog_: React.FC<PauseSubscriptionDialogProps> = ({ onClose, open, organisationId, pausedAt, odooLinked }) => {
  const [pause, { loading }] = useSuperadminPauseSubscriptionMutation();
  const { showToast } = useToastNotifications();
  const { formatDay } = useFormatDate();
  const failed = useT('could not change the pause', { swc: true });
  const paused = !!pausedAt;
  const onConfirm = useCallback(async () => {
    try {
      await pause({ variables: { organisationId, paused: !paused } });
    } catch (e) {
      showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
    }
  }, [failed, organisationId, pause, paused, showToast]);
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      loading={loading}
      variant={paused ? 'info' : 'warning'}
      title={paused ? <T _str="resume subscription" swc /> : <T _str="pause subscription" swc />}
      message={
        paused ? (
          <>
            <T _str="Paused since {date}. Resuming lifts the block. No date moves." args={{ date: formatDay(pausedAt) }} />
            {odooLinked && (
              <>
                {' '}
                <T _str="The Odoo subscription runs again." />
              </>
            )}
          </>
        ) : (
          <>
            <T _str="For a customer that did not pay. Uploads and orders are refused. The term runs on: monthly projects are granted, pending changes apply and it renews on its day." />
            {odooLinked && (
              <>
                {' '}
                <T _str="The Odoo subscription is paused too. It invoices nothing meanwhile." />
              </>
            )}
          </>
        )
      }
      confirmLabel={paused ? <T _str="resume" swc /> : <T _str="pause" swc />}
      cancelLabel={<T _str="keep as is" swc />}
    />
  );
};
export const PauseSubscriptionDialog = memo(PauseSubscriptionDialog_);

// ---------------------------------------------------------------------------------------------------------
// Cancel

interface CancelFormValues {
  immediately: boolean;
}

export interface CancelSubscriptionDialogProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  endDate?: DateValue | null;
  // The subscription is already set to end with the term.
  alreadyEnding: boolean;
  // Paused: the account is frozen, and cancelling means ending it now — the term voided, its projects taken away.
  paused?: boolean;
  odooLinked: boolean;
}

const CancelFields: React.FC<Pick<CancelSubscriptionDialogProps, 'endDate' | 'alreadyEnding' | 'paused' | 'odooLinked'>> = ({ endDate, alreadyEnding, paused, odooLinked }) => {
  const { values } = useFormikContext<CancelFormValues>();
  const { formatDay } = useFormatDate();
  const immediately = useT('end now and void the current term (the customer did not pay for it)', { swc: true });
  return (
    <div className="flex flex-col gap-3">
      {paused && (
        <InfoContent
          type="warning"
          title={<T _str="The subscription is paused" swc />}
          description={<T _str="A paused subscription is ended now. The term is voided and its projects are taken away. To let it run to its end instead, resume it first." />}
        />
      )}
      {alreadyEnding && !values.immediately && (
        <InfoContent type="info" title={<T _str="Already cancelled" swc />} description={<T _str="The subscription is set to end with the term. Confirming changes nothing. Tick the box to end it now instead." />} />
      )}
      <Section title={values.immediately ? <T _str="end now" swc /> : <T _str="at the end of the term" swc />} tone="highlight">
        <Hint>
          {values.immediately ? (
            <T _str="The term is voided today. Its monthly projects are removed. From now on the organisation buys projects as it needs them. Nothing is refunded or credited here. An invoice already posted is for sales to credit." />
          ) : (
            <T _str="The plan and its monthly projects run until {date}. Then the subscription ends instead of renewing. Nothing is billed or refunded." args={{ date: endDate ? formatDay(endDate) : '' }} />
          )}
        </Hint>
      </Section>
      <Section title={<T _str="in Odoo" swc />}>
        <Hint>
          {!odooLinked ? (
            <T _str="Not linked to an Odoo subscription: nothing changes there." />
          ) : values.immediately ? (
            <T _str="The subscription's end date is set to today: Odoo closes it and invoices no further cycle." />
          ) : (
            <T _str="The subscription's end date is set to {date}. Odoo closes it then and invoices no further cycle. Sales can call it off there, which calls it off here too." args={{ date: endDate ? formatDay(endDate) : '' }} />
          )}
        </Hint>
      </Section>
      {!paused && <FormikCheckBox name="immediately" label={immediately} hideErrorLabel />}
      {values.immediately && (
        <InfoContent
          type="warning-red"
          title={<T _str="The current term is voided now" swc />}
          description={<T _str="Its monthly projects are removed right away. Nothing of it counts as paid. Its pending changes, if any, are dropped." />}
        />
      )}
    </div>
  );
};

// Cancels the subscription: at the end of the term, or now.
const CancelSubscriptionDialog_: React.FC<CancelSubscriptionDialogProps> = ({ onClose, open, organisationId, endDate, alreadyEnding, paused = false, odooLinked }) => {
  const [cancel, { loading }] = useSuperadminCancelSubscriptionMutation();
  const { showToast } = useToastNotifications();
  const title = useT('cancel subscription', { swc: true });
  const confirm = useT('cancel subscription', { swc: true });
  const keep = useT('keep subscription', { swc: true });
  const failed = useT('could not cancel the subscription', { swc: true });
  const onSubmit: FormikConfig<CancelFormValues>['onSubmit'] = useCallback(
    async (values) => {
      try {
        await cancel({ variables: { organisationId, immediately: values.immediately || undefined } });
        onClose();
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [cancel, failed, onClose, organisationId, showToast],
  );
  const formik: FormikConfig<CancelFormValues> = useMemo(() => ({ initialValues: { immediately: paused }, onSubmit, enableReinitialize: true }), [onSubmit, paused]);
  return (
    <CreateModal title={title} createButtonTitle={confirm} cancelButtonTitle={keep} formik={formik} onClose={onClose} open={open} isSubmitting={loading} panelClassName="max-w-xl" showCloseIcon>
      <CancelFields endDate={endDate} alreadyEnding={alreadyEnding} paused={paused} odooLinked={odooLinked} />
    </CreateModal>
  );
};
export const CancelSubscriptionDialog = memo(CancelSubscriptionDialog_);
